import { lazy, Suspense } from 'react';
import { Link } from '@tanstack/react-router';
import { Lightbulb, Network, PenLine, Video } from 'lucide-react';
import type { EntityPreview } from '@nw/contracts';
import { Instrument, Readout } from '@nw/ui';
import { ApiError, api, useFormat, useHome, verbMatcher, type AssistantCommand, type WidgetDef } from '@nw/web-platform';
import type { Concept, MindNote, NetworkHome, RecentThought, VideoSource } from '../public/index.js';
import { useGraph } from './api.js';

const StaticRenderer = lazy(() => import('@nw/graph-renderer-2d').then((m) => ({ default: m.GraphRenderer2D })));
const msg = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);

export const mindCommands: AssistantCommand[] = [
  {
    id: 'mind.note',
    title: 'Capture a thought',
    usage: 'note <what you are thinking>',
    icon: PenLine,
    needsArg: true,
    match: verbMatcher(['note', 'thought', 'remember', 'write down', 'jot'], {
      argRequired: true,
    }),
    run: async (arg) => {
      try {
        const n = (await api<MindNote>('/mind/notes', { body: { body: arg } })).data;
        return {
          ok: true,
          message: 'Thought saved',
          href: `/mind/notes/${n.id}`,
        };
      } catch (err) {
        return {
          ok: false,
          message: msg(err, 'The thought could not be saved'),
        };
      }
    },
  },
  {
    id: 'mind.idea',
    title: 'Name an idea',
    usage: 'idea <name>',
    icon: Lightbulb,
    needsArg: true,
    match: verbMatcher(['idea', 'new idea', 'concept'], { argRequired: true }),
    run: async (arg) => {
      try {
        const c = (
          await api<Concept>('/mind/concepts', {
            body: { title: arg!.slice(0, 160) },
          })
        ).data;
        return {
          ok: true,
          message: `Added the idea “${c.title}”`,
          href: `/mind/concepts/${c.id}`,
        };
      } catch (err) {
        return { ok: false, message: msg(err, 'The idea could not be added') };
      }
    },
  },
  {
    id: 'mind.video',
    title: 'Keep a video',
    usage: 'video <YouTube link> [as <title>]',
    icon: Video,
    needsArg: true,
    match: (text) => {
      const m = verbMatcher(['video', 'save video', 'add video'], {
        argRequired: true,
      })(text);
      if (m) return m;
      return /^(https?:\/\/)?(www\.|m\.)?(youtube\.com|youtu\.be)\//i.test(text.trim()) ? { score: 0.92, arg: text.trim() } : null;
    },
    run: async (arg) => {
      const [url, title] = (arg ?? '').split(/\s+as\s+/i);
      try {
        const v = (
          await api<VideoSource>('/mind/sources', {
            body: {
              url: url!.trim(),
              ...(title ? { title: title.trim() } : {}),
            },
          })
        ).data;
        return {
          ok: true,
          message: `Saved “${v.title}”`,
          href: `/mind/sources/${v.id}`,
        };
      } catch (err) {
        if (err instanceof ApiError && (err.details as { needsManualTitle?: boolean } | undefined)?.needsManualTitle) {
          return {
            ok: false,
            message: `${err.message} Say “video ${url!.trim()} as <title>”.`,
          };
        }
        return { ok: false, message: msg(err, 'The video could not be saved') };
      }
    },
  },
  {
    id: 'mind.explore',
    title: 'Explore connections',
    usage: 'explore <book, idea or person>',
    icon: Network,
    needsArg: true,
    match: verbMatcher(['explore', 'connections of', 'network of', 'show connections of'], { argRequired: true }),
    run: async (arg, ctx) => {
      const hit = (await api<EntityPreview[]>(`/search?q=${encodeURIComponent(arg!)}&limit=3`)).data[0];
      if (!hit) return { ok: false, message: `Nothing matches “${arg}”.` };
      ctx.navigate(`/mind?root=${encodeURIComponent(hit.key)}&focus=${encodeURIComponent(hit.key)}`);
      return { ok: true, message: `Connections of ${hit.title}` };
    },
  },
];

function useSection<T>(kind: string) {
  const home = useHome();
  return home.data?.sections.find((s) => s.moduleId === 'mind-networks' && s.kind === kind)?.data as T | undefined;
}

function NetworkWidget({ size }: { size: string }) {
  const net = useSection<NetworkHome>('network');
  const view = net?.views[0];
  const graph = useGraph({
    root: view?.rootKey ?? undefined,
    limit: size === 'l' ? 40 : 24,
  });
  const snap = graph.data?.snapshot;
  return (
    <Instrument
      tone="smoke"
      label="Network"
      status={
        <>
          <span className="nw-live-dot" /> {view ? view.name : 'Recent'}
        </>
      }
      action={
        <Link to="/mind" className="text-xs opacity-70 hover:underline">
          Open
        </Link>
      }
    >
      <div data-surface="observatory" className="relative -mx-2 mt-1 min-h-0 flex-1">
        {snap && snap.nodes.length ? (
          <Suspense fallback={null}>
            <StaticRenderer snapshot={snap} selection={null} reducedMotion interactive={false} ariaLabel={`Network preview: ${snap.nodes.length} items`} />
          </Suspense>
        ) : (
          <p className="grid h-full place-items-center text-sm opacity-70">Connections appear as you link things.</p>
        )}
      </div>
      <div className="flex items-end gap-6">
        <Readout value={net?.entityCount ?? '—'} unit="items" />
        <Readout value={snap?.edges.length ?? '—'} unit="links in view" />
      </div>
    </Instrument>
  );
}

function ThoughtWidget() {
  const t = useSection<RecentThought>('recentThought');
  const fmt = useFormat();
  return (
    <Instrument label={t?.kind === 'concept' ? 'Latest idea' : 'Latest thought'}>
      {t ? (
        <>
          <p className="mt-2 line-clamp-3 text-[1.05rem] leading-snug font-medium">{t.title}</p>
          {t.excerpt && t.excerpt !== t.title ? <p className="line-clamp-2 text-sm text-[var(--nw-quiet)]">{t.excerpt}</p> : null}
          <Link to="/mind" search={{ focus: t.key, root: t.key }} className="mt-auto text-xs text-[var(--nw-quiet)] hover:underline">
            See what it connects to · {fmt.relative(t.updatedAt)}
          </Link>
        </>
      ) : (
        <p className="mt-auto text-sm text-[var(--nw-quiet)]">Say “note” and a thought to begin.</p>
      )}
    </Instrument>
  );
}

function ViewsWidget() {
  const net = useSection<NetworkHome>('network');
  return (
    <Instrument label="Saved views">
      <Readout value={net?.views.length ?? 0} unit="views" />
      <ul className="mt-auto grid gap-1">
        {(net?.views ?? []).slice(0, 2).map((v) => (
          <li key={v.id}>
            <Link
              to="/mind"
              search={{
                view: v.id,
                root: v.rootKey ?? undefined,
                focus: v.rootKey ?? undefined,
              }}
              className="block truncate text-sm hover:underline"
            >
              {v.name}
            </Link>
          </li>
        ))}
      </ul>
    </Instrument>
  );
}

export const mindWidgets: WidgetDef[] = [
  {
    id: 'mind.network',
    title: 'Network',
    description: 'A live view of your connections',
    sizes: ['m', 'l'],
    defaultSize: 'l',
    Component: NetworkWidget,
  },
  {
    id: 'mind.thought',
    title: 'Latest thought',
    description: 'What you were last thinking about',
    sizes: ['m'],
    defaultSize: 'm',
    Component: ThoughtWidget,
  },
  {
    id: 'mind.views',
    title: 'Saved views',
    description: 'Jump into a saved part of the network',
    sizes: ['s', 'm'],
    defaultSize: 's',
    Component: ViewsWidget,
  },
];

/** One live line for the Spaces grid and widget. */
export function useMindStatus(): string | null {
  const net = useSection<NetworkHome>('network');
  if (!net) return null;
  return `${net.entityCount} ${net.entityCount === 1 ? 'item' : 'items'}${net.views.length ? ` · ${net.views.length} views` : ''}`;
}
